import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getInvoice } from "../api/invoices";

const fmt = (n) =>
  "₹" +
  (n || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const fmtDate = (d) =>
  new Date(d).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

const STATUS_COLORS = {
  paid: { bg: "#d1fae5", color: "#065f46" },
  sent: { bg: "#dbeafe", color: "#1e40af" },
  draft: { bg: "#f3f4f6", color: "#6b7280" },
  overdue: { bg: "#fee2e2", color: "#991b1b" },
  cancelled: { bg: "#fef3c7", color: "#92400e" },
};

export default function InvoiceView() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getInvoice(id)
     .then((res) => {
 // console.log(res.data);

  setInvoice(res.data.invoice || res.data);
})
      .catch((err) => {
        console.error(err);
        alert("Failed to load invoice");
      })
      .finally(() => setLoading(false));
  }, [id]);

  const openPDFPreview = () => {
    window.open(
      `${process.env.REACT_APP_API_URL}/api/invoices/${id}/pdf`,
      "_blank"
    );
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loader"></div>
      </div>
    );
  }

  if (!invoice) {
    return <div className="not-found">Invoice not found</div>;
  }

  const statusStyle = STATUS_COLORS[invoice.status] || {
    bg: "#eee",
    color: "#333",
  };

  return (
    <>
      <style>{`
        *{
          box-sizing:border-box;
        }

        body{
          margin:0;
          font-family:Inter,sans-serif;
          background:#f3f4f8;
          color:#111827;
        }

        .page{
          min-height:100vh;
          padding:40px;
        }

        .container{
          max-width:1200px;
          margin:auto;
        }

        .topbar{
          display:flex;
          justify-content:space-between;
          align-items:center;
          margin-bottom:24px;
          flex-wrap:wrap;
          gap:16px;
        }

        .title{
          font-size:34px;
          font-weight:800;
          color:#111827;
        }

        .sub{
          color:#6b7280;
          margin-top:6px;
          font-size:14px;
        }

        .badge{
          padding:8px 18px;
          border-radius:999px;
          font-size:12px;
          font-weight:700;
          text-transform:uppercase;
          letter-spacing:1px;
        }

        .card{
          background:white;
          border-radius:24px;
          overflow:hidden;
          border:1px solid #e5e7eb;
          box-shadow:0 10px 40px rgba(0,0,0,0.05);
        }

        .header{
          background:linear-gradient(135deg,#2d2b69,#4f46e5);
          color:white;
          padding:40px;
          display:flex;
          justify-content:space-between;
          flex-wrap:wrap;
          gap:20px;
        }

        .brand{
          font-size:30px;
          font-weight:800;
        }

        .invoice-meta{
          text-align:right;
        }

        .invoice-number{
          font-size:24px;
          font-weight:700;
        }

        .invoice-date{
          margin-top:8px;
          opacity:0.9;
        }

        .section{
          padding:32px 40px;
          border-bottom:1px solid #f1f1f1;
        }

        .grid{
          display:grid;
          grid-template-columns:repeat(auto-fit,minmax(250px,1fr));
          gap:24px;
        }

        .label{
          font-size:12px;
          text-transform:uppercase;
          letter-spacing:1px;
          color:#9ca3af;
          margin-bottom:10px;
          font-weight:700;
        }

        .value{
          font-size:15px;
          line-height:1.8;
          color:#111827;
        }

        .value strong{
          display:block;
          font-size:18px;
          margin-bottom:6px;
        }

        table{
          width:100%;
          border-collapse:collapse;
        }

        thead{
          background:#111827;
          color:white;
        }

        th{
          padding:16px;
          font-size:12px;
          text-align:left;
          text-transform:uppercase;
          letter-spacing:1px;
        }

        td{
          padding:18px 16px;
          border-bottom:1px solid #f3f4f6;
          font-size:14px;
        }

        tbody tr:hover{
          background:#fafafa;
        }

        .amount{
          font-weight:700;
        }

        .totals{
          display:flex;
          justify-content:flex-end;
          padding:32px 40px;
        }

        .totals-card{
          width:360px;
          background:#f9fafb;
          border-radius:18px;
          padding:24px;
          border:1px solid #e5e7eb;
        }

        .total-row{
          display:flex;
          justify-content:space-between;
          margin-bottom:16px;
          font-size:15px;
          color:#374151;
        }

        .grand{
          border-top:1px solid #d1d5db;
          padding-top:18px;
          margin-top:18px;
          font-size:22px;
          font-weight:800;
          color:#111827;
        }

        .actions{
          padding:32px 40px;
          display:flex;
          gap:14px;
          flex-wrap:wrap;
        }

        .btn{
          border:none;
          padding:14px 22px;
          border-radius:14px;
          cursor:pointer;
          font-size:14px;
          font-weight:700;
          transition:0.2s;
        }

        .btn-primary{
          background:#2d2b69;
          color:white;
        }

        .btn-primary:hover{
          background:#4338ca;
          transform:translateY(-1px);
        }

        .btn-secondary{
          background:white;
          border:1px solid #d1d5db;
          color:#111827;
        }

        .btn-secondary:hover{
          background:#f9fafb;
        }

        .pdf-preview{
          width:100%;
          height:900px;
          border:none;
          background:#f3f4f6;
        }

        .loading-screen{
          min-height:100vh;
          display:grid;
          place-items:center;
          background:#f3f4f8;
        }

        .loader{
          width:40px;
          height:40px;
          border:4px solid #ddd;
          border-top-color:#4f46e5;
          border-radius:50%;
          animation:spin 0.8s linear infinite;
        }

        .not-found{
          padding:60px;
          text-align:center;
          font-size:20px;
        }

        @keyframes spin{
          to{
            transform:rotate(360deg);
          }
        }

        @media(max-width:768px){
          .page{
            padding:16px;
          }

          .section,
          .actions,
          .totals{
            padding:20px;
          }

          .header{
            padding:24px;
          }

          .invoice-meta{
            text-align:left;
          }

          .pdf-preview{
            height:600px;
          }
        }
      `}</style>

      <div className="page">
        <div className="container">

          <div className="topbar">
            <div>
              <div className="title">
                Invoice Preview
              </div>

              <div className="sub">
                Professional GST Invoice View
              </div>
            </div>

            <div
              className="badge"
              style={{
                background: statusStyle.bg,
                color: statusStyle.color,
              }}
            >
              {invoice.status}
            </div>
          </div>

          <div className="card">

            <div className="header">
              <div>
                <div className="brand">
                  Invoice AI
                </div>

                <div className="invoice-date">
                  Generated on {fmtDate(invoice.issueDate)}
                </div>
              </div>

              <div className="invoice-meta">
                <div className="invoice-number">
                  {invoice.invoiceNumber}
                </div>

                <div className="invoice-date">
                  Due: {fmtDate(invoice.dueDate)}
                </div>
              </div>
            </div>

            <div className="section">
              <div className="grid">

                <div>
                  <div className="label">Bill To</div>

                  <div className="value">
                    <strong>{invoice.client?.name}</strong>

                    {invoice.client?.address}
                    <br />

                    {invoice.client?.city}
                    <br />

                    {invoice.client?.phone}
                  </div>
                </div>

                <div>
                  <div className="label">Invoice Details</div>

                  <div className="value">
                    Invoice No: {invoice.invoiceNumber}
                    <br />

                    Issue Date: {fmtDate(invoice.issueDate)}
                    <br />

                    Due Date: {fmtDate(invoice.dueDate)}
                  </div>
                </div>

                <div>
                  <div className="label">Payment Summary</div>

                  <div className="value">
                    Total Amount
                    <br />

                    <strong>{fmt(invoice.totalAmount)}</strong>
                  </div>
                </div>

              </div>
            </div>

            <div className="section">
              <table>
                <thead>
                  <tr>
                    <th>Description</th>
                    <th>Qty</th>
                    <th>Rate</th>
                    <th>GST</th>
                    <th>Total</th>
                  </tr>
                </thead>

                <tbody>
                  {invoice.items?.map((item, i) => (
                    <tr key={i}>
                      <td>{item.description}</td>
                      <td>{item.quantity}</td>
                      <td>{fmt(item.rate)}</td>
                      <td>{item.gstRate}%</td>
                      <td className="amount">
                        {fmt(item.amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="totals">
              <div className="totals-card">

                <div className="total-row">
                  <span>Subtotal</span>
                  <span>{fmt(invoice.subtotal)}</span>
                </div>

                <div className="total-row">
                  <span>GST</span>
                  <span>{fmt(invoice.totalGst)}</span>
                </div>

                <div className="total-row grand">
                  <span>Total</span>
                  <span>{fmt(invoice.totalAmount)}</span>
                </div>

              </div>
            </div>

            <div className="actions">

              <button
                className="btn btn-secondary"
                onClick={() => navigate(-1)}
              >
                ← Back
              </button>

              <button
                className="btn btn-primary"
                onClick={openPDFPreview}
              >
                Open PDF Preview
              </button>

            </div>

            {/* LIVE PDF PREVIEW */}
            <iframe
              title="Invoice PDF"
              className="pdf-preview"
              src={`${process.env.REACT_APP_API_URL}/api/invoices/${id}/pdf`}
            />

          </div>
        </div>
      </div>
    </>
  );
}